import { getUSPSData } from '../util/uspsServiceLevels';
import {
  createAndVerifyAddress,
  fetchCarriersAndRates,
  getShippingCarriers,
  logAddressOverride,
  transitionPrivileged,
} from '../util/api';
import { types as sdkTypes } from '../util/sdkLoader';
import { transitions } from '../transactions/transactionProcessPurchase';
import { DOMESTIC, INTERNATIONAL, USPS } from '../util/enums';
import { storableError } from '../util/errors';
import { addMarketplaceEntities } from './marketplaceData.duck';
// ================ Action types ================ //

export const FETCH_SHIPPING_CARRIERS_REQUEST = 'app/shipping/FETCH_SHIPPING_CARRIERS_REQUEST';
export const FETCH_SHIPPING_CARRIERS_SUCCESS = 'app/shipping/FETCH_SHIPPING_CARRIERS_SUCCESS';
export const FETCH_SHIPPING_CARRIERS_ERROR = 'app/shipping/FETCH_SHIPPING_CARRIERS_ERROR';
export const ADD_CARRIER = 'app/shipping/ADD_CARRIER';
export const DELETE_CARRIER = 'app/shipping/DELETE_CARRIER';
export const UPDATE_CARRIER = 'app/shipping/UPDATE_CARRIER';
export const SET_INITIAL_CARRIERS = 'app/shipping/SET_INITIAL_CARRIERS';

export const GET_SHIPPING_CARRIERS_RATES_REQUEST =
  'app/shipping/GET_SHIPPING_CARRIERS_RATES_REQUEST';
export const GET_SHIPPING_CARRIERS_RATES_SUCCESS =
  'app/shipping/GET_SHIPPING_CARRIERS_RATES_SUCCESS';
export const GET_SHIPPING_CARRIERS_RATES_ERROR = 'app/shipping/GET_SHIPPING_CARRIERS_RATES_ERROR';

export const FETCH_LABEL_RATES_REQUEST = 'app/shipping/FETCH_LABEL_RATES_REQUEST';
export const FETCH_LABEL_RATES_SUCCESS = 'app/shipping/FETCH_LABEL_RATES_SUCCESS';
export const FETCH_LABEL_RATES_ERROR = 'app/shipping/FETCH_LABEL_RATES_ERROR';
export const CLEAR_LABEL_RATES = 'app/shipping/CLEAR_LABEL_RATES';

export const CREATE_ADDRESS_REQUEST = 'app/shipping/CREATE_ADDRESS_REQUEST';
export const CREATE_ADDRESS_SUCCESS = 'app/shipping/CREATE_ADDRESS_SUCCESS';
export const CREATE_ADDRESS_ERROR = 'app/shipping/CREATE_ADDRESS_ERROR';

export const PURCHASE_SHIPPING_LABEL_REQUEST = 'app/shipping/PURCHASE_SHIPPING_LABEL_REQUEST';
export const PURCHASE_SHIPPING_LABEL_SUCCESS = 'app/shipping/PURCHASE_SHIPPING_LABEL_SUCCESS';
export const PURCHASE_SHIPPING_LABEL_ERROR = 'app/shipping/PURCHASE_SHIPPING_LABEL_ERROR';
export const RESET_PURCHASE_LABEL = 'app/shipping/RESET_PURCHASE_LABEL';
export const CLEAR_LABEL_FOR_OTHER_ORDER = 'app/shipping/CLEAR_LABEL_FOR_OTHER_ORDER';

export const CHECK_CARRIERS_REQUEST = 'app/shipping/CHECK_CARRIERS_REQUEST';
export const CHECK_CARRIERS_DONE = 'app/shipping/CHECK_CARRIERS_DONE';

// ================ Reducer ================ //

const initialState = {
  // Checkout's "Use this address anyway": can any carrier reach the address?
  checkCarriersInProgress: false,
  fetchCarriersInProgress: false,
  carriers: null,
  priorityCarriers: [],
  fetchCarrierError: null,
  groupedCarriers: {
    domesticCarriers: [],
    internationalCarriers: [],
  },
  // Checkout's rates for the buyer's address
  carrierRates: [],
  getCarriersRatesInProgress: false,
  getCarriersRatesError: null,
  // The label window's rates for the package the seller entered, and the latest ask for
  // them: an older answer, or one arriving after the window opened afresh, is dropped
  labelRates: null,
  labelRatesInProgress: false,
  labelRatesError: null,
  labelRatesRequest: 0,
  createAddressInProgress: false,
  createAddressError: null,
  purchaseLabelInProgress: false,
  purchaseLabelError: null,
  // The label just bought, and the order it was bought for (labelInfoFor):
  // the order page shows it only on that order's page.
  labelInfo: null,
  labelInfoFor: null,
};

// EasyPost's carriers that sellers can offer in the listing UI: USPS, UPS, FedEx, DHL,
// Canada Post. It may return others (test or staging carriers) that are never shown.
const OFFERED_CARRIERS = ['usps', 'ups', 'fedex', 'dhl', 'canadapost'];
const isOfferedCarrier = carrier => {
  const name = typeof carrier?.name === 'string' ? carrier.name.toLowerCase() : '';
  return OFFERED_CARRIERS.some(offered => name.includes(offered));
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  const {
    newCarrier = [],
    carrierType = '',
    carrierId = '',
    currentCarrier = {},
    updatedCarrier = {},
  } = payload || {};
  const carrierKey = 'groupedCarriers';
  const updatedCarrierType = `${carrierType.toLowerCase()}Carriers`;
  const existingCarriers = state[carrierKey][updatedCarrierType] || [];

  switch (type) {
    case CREATE_ADDRESS_REQUEST:
      return { ...state, createAddressInProgress: true, createAddressError: null };
    case CREATE_ADDRESS_SUCCESS:
      return { ...state, createAddressInProgress: false };
    case CREATE_ADDRESS_ERROR:
      return { ...state, createAddressInProgress: false, createAddressError: payload };
    case PURCHASE_SHIPPING_LABEL_REQUEST:
      return { ...state, purchaseLabelInProgress: true, purchaseLabelError: null };
    case PURCHASE_SHIPPING_LABEL_SUCCESS:
      return {
        ...state,
        purchaseLabelInProgress: false,
        labelInfo: payload.labelInfo,
        labelInfoFor: payload.transactionId,
        purchaseLabelError: null,
      };
    case PURCHASE_SHIPPING_LABEL_ERROR:
      return {
        ...state,
        purchaseLabelInProgress: false,
        purchaseLabelError: payload,
        labelInfo: null,
        labelInfoFor: null,
      };
    case RESET_PURCHASE_LABEL:
      return {
        ...state,
        purchaseLabelInProgress: false,
        purchaseLabelError: null,
        labelInfo: null,
        labelInfoFor: null,
      };
    case CLEAR_LABEL_FOR_OTHER_ORDER:
      return state.labelInfoFor && state.labelInfoFor !== payload
        ? { ...state, labelInfo: null, labelInfoFor: null }
        : state;
    case CHECK_CARRIERS_REQUEST:
      return { ...state, checkCarriersInProgress: true };
    case CHECK_CARRIERS_DONE:
      return { ...state, checkCarriersInProgress: false };
    case GET_SHIPPING_CARRIERS_RATES_REQUEST:
      return { ...state, getCarriersRatesInProgress: true, getCarriersRatesError: null };
    case GET_SHIPPING_CARRIERS_RATES_SUCCESS:
      return { ...state, getCarriersRatesInProgress: false, carrierRates: payload };
    case GET_SHIPPING_CARRIERS_RATES_ERROR:
      return { ...state, getCarriersRatesInProgress: false, getCarriersRatesError: payload };
    case FETCH_LABEL_RATES_REQUEST:
      return {
        ...state,
        labelRatesInProgress: true,
        labelRatesError: null,
        labelRatesRequest: payload,
      };
    case FETCH_LABEL_RATES_SUCCESS:
      return action.meta?.request !== state.labelRatesRequest
        ? state
        : { ...state, labelRatesInProgress: false, labelRates: payload };
    case FETCH_LABEL_RATES_ERROR:
      return action.meta?.request !== state.labelRatesRequest
        ? state
        : { ...state, labelRatesInProgress: false, labelRatesError: payload };
    case CLEAR_LABEL_RATES:
      return {
        ...state,
        labelRates: null,
        labelRatesInProgress: false,
        labelRatesError: null,
        labelRatesRequest: 0,
      };
    case FETCH_SHIPPING_CARRIERS_REQUEST:
      return { ...state, fetchCarrierError: null, fetchCarriersInProgress: true };
    case FETCH_SHIPPING_CARRIERS_SUCCESS: {
      const carriers = Array.isArray(payload) ? payload : [];
      const priorityCarriers = carriers.filter(isOfferedCarrier);
      // USPS is every listing's default; without it (the account's USPS off or renamed)
      // the listing starts with no default carriers
      const uspsCarrier = priorityCarriers.find(carrier => carrier.name === USPS);
      const { _params, ...cleanedCarrier } = uspsCarrier || {};
      return {
        ...state,
        fetchCarriersInProgress: false,
        carriers,
        priorityCarriers,
        groupedCarriers: uspsCarrier
          ? {
              domesticCarriers: getUSPSData(cleanedCarrier, DOMESTIC),
              internationalCarriers: getUSPSData(cleanedCarrier, INTERNATIONAL),
            }
          : initialState.groupedCarriers,
      };
    }
    case FETCH_SHIPPING_CARRIERS_ERROR:
      return {
        ...state,
        fetchCarrierError: payload,
        fetchCarriersInProgress: false,
      };
    case UPDATE_CARRIER:
      // Ensure the carrier array exists before updating

      const newUpdatedCarriers = {
        ...state[carrierKey],
        [updatedCarrierType]: existingCarriers.map(carrier =>
          carrier.name === currentCarrier?.name ? updatedCarrier : carrier
        ),
      };

      return {
        ...state,
        [carrierKey]: newUpdatedCarriers,
      };

    case ADD_CARRIER:
      const updatedCarriers = {
        ...state[carrierKey],
        [updatedCarrierType]: [
          ...(state[carrierKey][updatedCarrierType] || []), // Ensure the array is initialized
          newCarrier,
        ],
      };

      return {
        ...state,
        [carrierKey]: updatedCarriers,
      };

    case DELETE_CARRIER:
      // Ensure the carrier array exists before filtering
      const filteredCarriers = {
        ...state[carrierKey],
        [updatedCarrierType]: existingCarriers.filter(carrier => carrier.name !== carrierId),
      };

      return {
        ...state,
        [carrierKey]: filteredCarriers,
      };
    case SET_INITIAL_CARRIERS:
      // Normalize old 4-array format (defaultDomesticCarrier, domesticAdditional, etc.)
      // into the new 2-array format for backwards compatibility with saved listings/profiles
      const normDomestic = payload.domesticCarriers || [
        ...(payload.defaultDomesticCarrier || []),
        ...(payload.domesticAdditional || []),
      ];
      const normInternational = payload.internationalCarriers || [
        ...(payload.defaultInternationalCarrier || []),
        ...(payload.internationalAdditional || []),
      ];
      return {
        ...state,
        groupedCarriers: {
          domesticCarriers: normDomestic,
          internationalCarriers: normInternational,
        },
      };
    default:
      return state;
  }
}

// ================ Selectors ================ //

export const getCarriersSelector = state => {
  const {
    carriers,
    fetchCarrierError,
    fetchCarriersInProgress,
    priorityCarriers,
    groupedCarriers,
    carrierRates,
  } = state.shipping;
  return {
    carriers,
    fetchCarrierError,
    fetchCarriersInProgress,
    priorityCarriers,
    groupedCarriers,
    carrierRates,
  };
};

/**
 * A label just bought on an order page, with the order it was bought for:
 * { transactionId, label }, or null. Compare with shallowEqual (a new object each read).
 */
export const labelBoughtSelector = state => {
  const { labelInfo, labelInfoFor } = state.shipping;
  return labelInfo ? { transactionId: labelInfoFor, label: labelInfo } : null;
};

// ================ Action creators ================ //

export const fetchShippingCarriersRequest = () => ({ type: FETCH_SHIPPING_CARRIERS_REQUEST });

export const fetchShippingCarriersSuccess = payload => ({
  type: FETCH_SHIPPING_CARRIERS_SUCCESS,
  payload,
});

export const fetchShippingCarriersError = e => ({
  type: FETCH_SHIPPING_CARRIERS_ERROR,
  payload: e,
  error: true,
});

export const getShippingCarriersRatesRequest = () => ({
  type: GET_SHIPPING_CARRIERS_RATES_REQUEST,
});

export const getShippingCarriersRatesSuccess = payload => ({
  type: GET_SHIPPING_CARRIERS_RATES_SUCCESS,
  payload,
});

export const getShippingCarriersRatesError = e => ({
  type: GET_SHIPPING_CARRIERS_RATES_ERROR,
  payload: e,
  error: true,
});

const fetchLabelRatesRequest = request => ({ type: FETCH_LABEL_RATES_REQUEST, payload: request });
const fetchLabelRatesSuccess = (request, payload) => ({
  type: FETCH_LABEL_RATES_SUCCESS,
  payload,
  meta: { request },
});
const fetchLabelRatesError = (request, e) => ({
  type: FETCH_LABEL_RATES_ERROR,
  error: true,
  payload: e,
  meta: { request },
});
/** The label window opens afresh: its rates, and any answer on its way, go. */
export const clearLabelRates = () => ({ type: CLEAR_LABEL_RATES });

export const createAddressRequest = () => ({
  type: CREATE_ADDRESS_REQUEST,
});

export const createAddressSuccess = payload => ({
  type: CREATE_ADDRESS_SUCCESS,
  payload,
});

export const createAddressError = e => ({
  type: CREATE_ADDRESS_ERROR,
  payload: e,
  error: true,
});

export const purchaseShippingLabelRequest = () => ({
  type: PURCHASE_SHIPPING_LABEL_REQUEST,
});

export const purchaseShippingLabelSuccess = (labelInfo, transactionId) => ({
  type: PURCHASE_SHIPPING_LABEL_SUCCESS,
  payload: { labelInfo, transactionId },
});

export const purchaseShippingLabelError = e => ({
  type: PURCHASE_SHIPPING_LABEL_ERROR,
  payload: e,
  error: true,
});

export const resetPurchaseLabel = () => ({ type: RESET_PURCHASE_LABEL });

/** An order page opens: a label bought for another order is forgotten (a purchase in flight is not). */
export const clearLabelBoughtForOtherOrder = transactionId => ({
  type: CLEAR_LABEL_FOR_OTHER_ORDER,
  payload: transactionId,
});

const addCarrierAction = (newCarrier, carrierType) => ({
  type: ADD_CARRIER,
  payload: { newCarrier, carrierType },
});

const deleteCarrierAction = (carrierId, carrierType) => ({
  type: DELETE_CARRIER,
  payload: { carrierId, carrierType },
});
export const setInitialCarriers = data => ({
  type: SET_INITIAL_CARRIERS,
  payload: data,
});

// ================ Thunks ================ //

export const fetchShippingCarriers = (listing = {}, defaultCarriers = null) => async (
  dispatch,
  getState,
  sdk
) => {
  dispatch(fetchShippingCarriersRequest());
  try {
    const { statusCode, data } = await getShippingCarriers();
    if (statusCode === 200) {
      dispatch(fetchShippingCarriersSuccess(data));
    }
    if (listing.id) {
      // Existing listing: always use its own saved carriers
      const { shippingCarriers = {} } = listing?.attributes?.publicData || {};
      dispatch(setInitialCarriers(shippingCarriers));
    } else if (defaultCarriers && Object.keys(defaultCarriers).length) {
      // New listing: fall back to seller's profile defaults if available
      dispatch(setInitialCarriers(defaultCarriers));
    }
    // else: new listing with no profile defaults → keep USPS default from SUCCESS
  } catch (error) {
    dispatch(fetchShippingCarriersError(error));
  }
};
export const addCarrier = (newCarrier, carrierType) => {
  return dispatch => {
    dispatch(addCarrierAction(newCarrier, carrierType));
  };
};

export const deleteCarrier = (carrierId, carrierType) => {
  return dispatch => {
    dispatch(deleteCarrierAction(carrierId, carrierType));
  };
};

export const createAddress = params => async (dispatch, getState, sdk) => {
  dispatch(createAddressRequest());
  try {
    const response = await createAndVerifyAddress(params);
    dispatch(createAddressSuccess());
    return response?.data?.id; //Shipping address id
  } catch (error) {
    dispatch(createAddressError(storableError(error)));
  }
};

/** Checkout's rates for the buyer's address. Resolves with them; a failure rejects. */
export const getShippingCarriersAndRates = params => async (dispatch, getState, sdk) => {
  dispatch(getShippingCarriersRatesRequest());
  try {
    const response = await fetchCarriersAndRates(params);
    dispatch(getShippingCarriersRatesSuccess(response?.data));
    return response?.data; //rates
  } catch (error) {
    dispatch(getShippingCarriersRatesError(storableError(error)));
    // Checkout says why where it asked
    throw error;
  }
};

/**
 * The label window's rates for the package the seller entered (the same carriers'
 * rates as checkout's, for the label's addresses). Resolves with the rates when this
 * is still the window's latest ask, else with null (an older ask, or the window opened
 * afresh): the answer is in the state either way. A failure resolves with null too,
 * its reason in labelRatesError: the server's own (`reason`) when it gave one.
 */
let labelRatesRequests = 0;
export const fetchLabelRates = params => (dispatch, getState) => {
  labelRatesRequests += 1;
  const request = labelRatesRequests;
  dispatch(fetchLabelRatesRequest(request));
  const isLatest = () => getState().shipping.labelRatesRequest === request;
  return fetchCarriersAndRates(params)
    .then(response => {
      const rates = Array.isArray(response?.data) ? response.data : [];
      dispatch(fetchLabelRatesSuccess(request, rates));
      return isLatest() ? rates : null;
    })
    .catch(error => {
      // util/api puts the server's reason in `error`; a request that never got there has
      // its own message
      const reason = error?.error || error?.message || null;
      dispatch(fetchLabelRatesError(request, { ...storableError(error), reason }));
      return null;
    });
};

/**
 * Can any carrier reach this address? Checkout asks before an unverified address is
 * accepted ("Use this address anyway"). Resolves with the rates found, without making
 * them checkout's rates: the buyer is still in the address form.
 */
export const checkCarriersForAddress = params => dispatch => {
  dispatch({ type: CHECK_CARRIERS_REQUEST });
  return fetchCarriersAndRates(params)
    .then(response => (Array.isArray(response?.data) ? response.data : []))
    .finally(() => dispatch({ type: CHECK_CARRIERS_DONE }));
};

/**
 * Records a "Use this address anyway" override in the admin panel's audit log.
 * Resolves with its id, or null: logging never blocks the buyer (the address
 * carries its override flag either way).
 */
export const recordAddressOverride = details => () =>
  logAddressOverride(details)
    .then(result => result?.overrideId || null)
    .catch(() => null);

// The order as the page shows it, read again after a label step: the label
// itself is written to the order just after the step's transition.
const refreshTransaction = (sdk, transactionId) =>
  sdk.transactions.show({
    id: transactionId,
    include: [
      'customer',
      'customer.profileImage',
      'provider',
      'provider.profileImage',
      'listing',
      'listing.currentStock',
      'booking',
    ],
  });

/**
 * Buy the order's shipping label: one step on the server, which prices the
 * label from the carrier's rate, checks it is paid for (from this order's
 * earnings, by the card payment given, or both), buys it and records it on
 * the order. `deductionAmount` above zero means "take this much from earnings".
 */
export const purchaseLabel = params => async (dispatch, getState, sdk) => {
  dispatch(purchaseShippingLabelRequest());
  const { transactionId, ...input } = params;
  const fromEarnings = Number(input.deductionAmount) > 0;
  try {
    const response = await transitionPrivileged({
      bodyParams: {
        id: new sdkTypes.UUID(transactionId),
        transition: fromEarnings
          ? transitions.PROVIDER_BUY_LABEL_WITH_BALANCE
          : transitions.PROVIDER_BUY_LABEL,
        params: input,
      },
      queryParams: { expand: true },
    });
    dispatch(addMarketplaceEntities(response));
    dispatch(purchaseShippingLabelSuccess(response?.result?.labelInfo, transactionId));
    try {
      dispatch(addMarketplaceEntities(await refreshTransaction(sdk, transactionId)));
    } catch (refreshError) {
      // The label is bought and shown; a reload picks up the rest.
    }
    return response?.result;
  } catch (error) {
    const errorPayload = storableError(error);
    dispatch(purchaseShippingLabelError(errorPayload));
    throw error; // Re-throw for component handling
  }
};
